/**
 * Skills Panel Component
 *
 * Modal panel for viewing and assessing a rider's skills.
 * - Displays skills organized by Section → Category
 * - Three-button toggle for assessment level
 * - Expandable history timeline per skill
 * - Group Readiness recommendation (locked to rider's assigned group)
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.6, 2.7, 3.2, 4.1, 4.2, 4.3, 4.4, 4.5, 4.7, 4.8, 5.2, 5.3, 5.4, 5.6, 5.7
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SkillConfig, SkillAssessment, GroupReadiness } from '../../../types/skills';
import { ASSESSMENT_LEVEL_DISPLAY_MAP } from '../../../types/skills';
import type { Rider, Group } from '../../../api/rest-client';
import { Modal } from '../../common/Modal';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { useSkillsStore } from '../../../store/skillsStore';
import { restClient } from '../../../api/rest-client';
import { useAuth } from '../../../auth/AuthProvider';
import { computeGroupReadiness, getLatestAssessments } from '../../../utils/skillsUtils';
import { TimeTrialAssessment } from './TimeTrialAssessment';

interface SkillsPanelProps {
  rider: Rider;
  onClose: () => void;
}

const ASSESSMENT_LEVELS = ['Beginner', 'Intermediate', 'Advanced'] as const;

// Module-level caches for reference data (shared across all panel opens)
let cachedGroups: Group[] | null = null;
let cachedGroupsPromise: Promise<Group[]> | null = null;
let cachedUserNameMap: Map<string, string> | null = null;
let cachedUsersPromise: Promise<Map<string, string>> | null = null;

function fetchGroupsCached(): Promise<Group[]> {
  if (cachedGroups) return Promise.resolve(cachedGroups);
  if (cachedGroupsPromise) return cachedGroupsPromise;
  cachedGroupsPromise = restClient.getGroups().then(g => { cachedGroups = g; cachedGroupsPromise = null; return g; });
  return cachedGroupsPromise;
}

function fetchUserNameMapCached(): Promise<Map<string, string>> {
  if (cachedUserNameMap) return Promise.resolve(cachedUserNameMap);
  if (cachedUsersPromise) return cachedUsersPromise;
  cachedUsersPromise = restClient.getUsersMinimal().then(users => {
    const map = new Map<string, string>();
    for (const u of users) {
      const displayName = (u.name || '').trim();
      if (!displayName) continue;
      if (u.email) map.set(u.email.toLowerCase(), displayName);
      if (u.username) map.set(u.username.toLowerCase(), displayName);
    }
    cachedUserNameMap = map;
    cachedUsersPromise = null;
    return map;
  });
  return cachedUsersPromise;
}

/**
 * Organize skills into Section → Category → Skills hierarchy
 */
function organizeSkills(skills: SkillConfig[]): Map<string, Map<string, SkillConfig[]>> {
  const organized = new Map<string, Map<string, SkillConfig[]>>();
  for (const skill of skills) {
    if (!organized.has(skill.section)) organized.set(skill.section, new Map());
    const section = organized.get(skill.section)!;
    if (!section.has(skill.category)) section.set(skill.category, []);
    section.get(skill.category)!.push(skill);
  }
  return organized;
}

/**
 * Get color classes for assessment level
 */
export function getLevelColor(level: string | null): string {
  switch (level) {
    case 'Beginner': return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'Intermediate': return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'Advanced': return 'bg-green-100 text-green-800 border-green-300';
    default: return 'bg-gray-100 text-gray-500 border-gray-300';
  }
}

/**
 * Get button classes for assessment level toggle
 */
export function getLevelButtonClasses(level: typeof ASSESSMENT_LEVELS[number], isActive: boolean): string {
  if (!isActive) return 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50';
  switch (level) {
    case 'Beginner': return 'bg-orange-500 text-white border-orange-500';
    case 'Intermediate': return 'bg-blue-500 text-white border-blue-500';
    case 'Advanced': return 'bg-green-500 text-white border-green-500';
  }
}

/**
 * Get readiness suggestion color
 */
function getReadinessColor(suggestion: GroupReadiness['suggestion']): string {
  switch (suggestion) {
    case 'Ready to advance': return 'bg-green-50 border-green-200 text-green-800';
    case 'On track': return 'bg-blue-50 border-blue-200 text-blue-800';
    case 'Needs more time': return 'bg-orange-50 border-orange-200 text-orange-800';
    case 'Insufficient assessments': return 'bg-gray-50 border-gray-200 text-gray-600';
    case 'Top group — mastery achieved': return 'bg-purple-50 border-purple-200 text-purple-800';
  }
}

/**
 * Format ISO date string for display
 */
function formatDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

export function SkillsPanel({ rider, onClose }: SkillsPanelProps) {
  const { user } = useAuth();
  const { skillsConfig, configLoading, assessments, assessmentsLoading, fetchSkillsConfig, fetchAssessments, createAssessment, timeTrialZone, fetchTimeTrialZone } = useSkillsStore();

  const [groups, setGroups] = useState<Group[]>([]);
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set());
  const [savingSkillId, setSavingSkillId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userNameMap, setUserNameMap] = useState<Map<string, string>>(new Map());

  // Fetch groups on mount (module-level cache avoids repeat calls)
  useEffect(() => {
    fetchGroupsCached().then(setGroups).catch(err => {
      console.error('Failed to fetch groups:', err);
    });
  }, []);

  // Fetch users for coach name resolution (module-level cache avoids repeat calls)
  useEffect(() => {
    fetchUserNameMapCached().then(setUserNameMap).catch(() => {});
  }, []);

  // Fetch skills config for rider's group
  useEffect(() => {
    if (rider.group) {
      fetchSkillsConfig(rider.group);
    }
  }, [rider.group, fetchSkillsConfig]);

  // Fetch assessments for rider on mount
  useEffect(() => {
    fetchAssessments(rider.rider_id);
  }, [rider.rider_id, fetchAssessments]);

  // Fetch time-trial zone on mount (skip if rider has no group)
  useEffect(() => {
    if (rider.group) {
      fetchTimeTrialZone(rider.rider_id, rider.group);
    }
  }, [rider.rider_id, rider.group, fetchTimeTrialZone]);

  const skills = skillsConfig[rider.group] || [];
  const riderAssessments = assessments[rider.rider_id] || [];
  const latestAssessments = useMemo(() => getLatestAssessments(riderAssessments), [riderAssessments]);
  const organized = useMemo(() => organizeSkills(skills), [skills]);

  // Time-trial zone data for this rider
  const zoneData = rider.group ? timeTrialZone[rider.rider_id] : undefined;

  // Group assessments by skill_id for history
  const assessmentsBySkill = useMemo(() => {
    const map = new Map<string, SkillAssessment[]>();
    for (const a of riderAssessments) {
      if (!map.has(a.skill_id)) map.set(a.skill_id, []);
      map.get(a.skill_id)!.push(a);
    }
    // Sort each skill's assessments chronologically
    for (const [, arr] of map) {
      arr.sort((a, b) => a.assessed_at.localeCompare(b.assessed_at));
    }
    return map;
  }, [riderAssessments]);

  // Compute readiness
  const readiness = useMemo(
    () => computeGroupReadiness(
      skills,
      latestAssessments,
      groups,
      rider.group,
      zoneData?.zone
    ),
    [skills, latestAssessments, groups, rider.group, zoneData?.zone]
  );

  // Compute level counts from latest assessments
  const levelCounts = useMemo(() => {
    const counts = { 'Beginner': 0, 'Intermediate': 0, 'Advanced': 0 };
    for (const assessment of latestAssessments.values()) {
      counts[assessment.assessment_level]++;
    }
    return counts;
  }, [latestAssessments]);

  const toggleHistory = useCallback((skillId: string) => {
    setExpandedSkills(prev => {
      const next = new Set(prev);
      if (next.has(skillId)) next.delete(skillId);
      else next.add(skillId);
      return next;
    });
  }, []);

  const handleAssessment = useCallback(async (skillId: string, level: typeof ASSESSMENT_LEVELS[number]) => {
    setError(null);
    setSavingSkillId(skillId);
    try {
      await createAssessment({
        rider_id: rider.rider_id,
        skill_id: skillId,
        assessment_level: level,
        assessed_by: user?.email || user?.username || 'unknown',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save assessment');
    } finally {
      setSavingSkillId(null);
    }
  }, [rider.rider_id, user, createAssessment]);

  const isLoading = configLoading || assessmentsLoading;

  /** Resolve assessed_by email/username to "Coach [Full Name]" */
  const formatAssessor = useCallback((assessedBy: string) => {
    const name = userNameMap.get(assessedBy.toLowerCase());
    if (name) return `Coach ${name}`;
    // Fallback if user map hasn't loaded yet or no match
    return `Coach ${assessedBy}`;
  }, [userNameMap]);

  return (
    <Modal isOpen onClose={onClose} title={`Skills — ${rider.name}`} className="max-w-4xl">
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {/* Group label */}
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-gray-700">Group:</span>
            <span className="text-base text-gray-900">{rider.group}</span>
          </div>

          {/* Error banner */}
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
              {error}
              <button onClick={() => setError(null)} className="ml-2 underline" aria-label="Dismiss error">Dismiss</button>
            </div>
          )}

          {/* No skills message */}
          {skills.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No skills defined for this group</p>
          ) : (
            <>
              {/* Group Readiness */}
              <div className={`rounded-md border p-3 sm:p-4 ${getReadinessColor(readiness.suggestion)}`}>
                <div className="text-base">
                  <span className="font-semibold">Recommendation: </span>
                  <span>
                    {readiness.suggestion}
                    {readiness.targetGroup && ` → ${readiness.targetGroup}`}
                  </span>
                </div>
                <p className="text-sm mt-1 opacity-75">
                  {readiness.assessedCount}/{readiness.totalCount} assessed
                  {readiness.masteryPercent > 0 && `, ${Math.round(readiness.masteryPercent)}% mastery`}
                </p>
                {readiness.timeTrialAdjustment && (
                  <p className="text-sm mt-1 opacity-75">{readiness.timeTrialAdjustment}</p>
                )}
              </div>

              {/* Time Trial Assessment */}
              {zoneData && <TimeTrialAssessment data={zoneData} />}

              {/* Levels Breakdown */}
              <div className="space-y-2 text-base">
                <p className="font-semibold text-gray-700">Levels</p>
                <div className="space-y-0.5">
                  <p className="text-orange-700">{levelCounts['Beginner']} {ASSESSMENT_LEVEL_DISPLAY_MAP['Beginner']}</p>
                  <p className="text-blue-700">{levelCounts['Intermediate']} {ASSESSMENT_LEVEL_DISPLAY_MAP['Intermediate']}</p>
                  <p className="text-green-700">{levelCounts['Advanced']} {ASSESSMENT_LEVEL_DISPLAY_MAP['Advanced']}</p>
                </div>
              </div>

              {/* Skills by Section → Category */}
              {Array.from(organized.entries()).map(([section, categories]) => (
                <div key={section}>
                  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-1 mb-3">{section}</h3>
                  {Array.from(categories.entries()).map(([category, categorySkills]) => (
                    <div key={category} className="mb-4 ml-0 sm:ml-2">
                      <h4 className="text-base font-semibold text-gray-600 uppercase tracking-wide mb-2">{category}</h4>
                      <div className="space-y-2">
                        {categorySkills.map(skill => {
                          const latest = latestAssessments.get(skill.skill_id);
                          const skillHistory = assessmentsBySkill.get(skill.skill_id) || [];
                          const isExpanded = expandedSkills.has(skill.skill_id);
                          const isSaving = savingSkillId === skill.skill_id;

                          return (
                            <div key={skill.skill_id} className="rounded-md border border-gray-200 p-2 sm:p-3">
                              {/* Skill header */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium text-base text-gray-900">{skill.skill_name}</span>
                                    {!latest && (
                                      <span className="text-sm px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Not Assessed</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-500 mt-0.5">{skill.description}</p>
                                  {latest && (
                                    <p className="text-sm text-gray-400 mt-1">
                                      Last: {formatDate(latest.assessed_at)} by {formatAssessor(latest.assessed_by)}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Assessment toggle buttons */}
                              <div className="flex flex-row gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                                {ASSESSMENT_LEVELS.map(level => (
                                  <button
                                    type="button"
                                    key={level}
                                    onClick={() => handleAssessment(skill.skill_id, level)}
                                    disabled={isSaving}
                                    className={`flex-1 min-h-[3rem] sm:min-h-[4rem] text-sm px-1 rounded-lg border font-medium transition-colors flex items-center justify-center text-center leading-tight ${getLevelButtonClasses(level, latest?.assessment_level === level)} disabled:opacity-50`}
                                    aria-label={`Set ${skill.skill_name} to ${level}`}
                                  >
                                    {isSaving ? '…' : ASSESSMENT_LEVEL_DISPLAY_MAP[level]}
                                  </button>
                                ))}
                              </div>

                              {/* History toggle */}
                              {skillHistory.length > 0 && (
                                <button
                                  onClick={() => toggleHistory(skill.skill_id)}
                                  className="text-sm text-primary-600 hover:text-primary-800 mt-3 py-1"
                                  aria-label={`${isExpanded ? 'Hide' : 'Show'} history for ${skill.skill_name}`}
                                >
                                  {isExpanded ? '▾ Hide history' : `▸ History (${skillHistory.length})`}
                                </button>
                              )}

                              {/* Expanded history timeline */}
                              {isExpanded && skillHistory.length > 0 && (
                                <div className="mt-2 ml-2 border-l-2 border-gray-200 pl-3 space-y-2">
                                  {[...skillHistory].reverse().map((a, i) => (
                                    <div key={`${a.skill_timestamp}-${i}`} className="flex items-center gap-2 text-sm">
                                      <span className={`px-1.5 py-0.5 rounded border ${getLevelColor(a.assessment_level)}`}>
                                        {ASSESSMENT_LEVEL_DISPLAY_MAP[a.assessment_level]}
                                      </span>
                                      <span className="text-gray-400">{formatDate(a.assessed_at)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
